<script setup>
import {reactive,onMounted, nextTick, ref} from 'vue';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const form = ref(null);

const data = reactive({
  expanded: [],
  singleExpand: false,
  qualityHeaders: [
    {
      title: 'Name',
      align: 'start',
      sortable: false,
      key: 'name',
    },
    { title: 'Date', key: 'created_at' },
    { title: 'Quality Ratings', key: 'rating' },
    { title: 'Note', key: 'data-table-expand' },
    { title: 'Actions', align: 'center', key: 'actions', sortable: false },
  ],
  timelinessHeaders: [
    {
      title: 'Name',
      align: 'start',
      sortable: false,
      key: 'name',
    },
    { title: 'Date', key: 'created_at' },
    { title: 'Timeliness Ratings', key: 'rating' },
    { title: 'Note', key: 'data-table-expand' },
    { title: 'Actions', align: 'center', key: 'actions', sortable: false },
  ],
  attitudeHeaders: [
    {
      title: 'Name',
      align: 'start',
      sortable: false,
      key: 'name',
    },
    { title: 'Date', key: 'created_at' },
    { title: 'Attitude Ratings', key: 'rating' },
    { title: 'Note', key: 'data-table-expand' },
    { title: 'Actions', align: 'center', key: 'actions', sortable: false },
  ],
  valid: true,
  valid2: true,
  valid3: true,
  avgAttitudeRating: '',
  avgQualityRating: '',
  avgTimelinessRating: '',
  length: 5,
  listAttitudeOpen: false,
  listQualityOpen: false,
  listTimelinessOpen: false,
  openAttitudeRatingsForm: false,
  openQualityRatingsForm: false,
  openTimelinessRatingsForm: false,
  loading: false,
  show: [],
  ratings: [],
  ratings2: [],
  ratings3: [],
  loaded: true,
  isEditing: false,
  search: '',
  editedItem: {
    note: '',
    rating: '',
    category: '',
  },
  defaultItem: {
    note: '',
    rating: '',
    category: '',
  },
})

const listAll = () => {
  ApiWrapper.post('ratings-categories','list-ratings', {
        personId: props.contextIdA
      })
      .then((response) => {
        if(response.data){
          data.ratings = [];
          data.ratings2 = [];
          data.ratings3 = [];
          for(let x in response.data) {
            if(response.data[x].category === 'quality') {
              data.ratings.push(response.data[x])
            } else if(response.data[x].category === 'timeliness') {
              data.ratings2.push(response.data[x])
            } else if(response.data[x].category === 'attitude') {
              data.ratings3.push(response.data[x])
            }
          }
          averageQualityRating(data.ratings);
          averageTimelinessRating(data.ratings2);
          averageAttitudeRating(data.ratings3);
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false
      })
}
// Average Ratings
const averageAttitudeRating = (array) => {
  let sum = 0;
  for (let item in array) {
    sum += array[item].rating * 1;
  }
  data.avgAttitudeRating =
      Math.round((sum / array.length + Number.EPSILON) * 100) / 100;
}
const averageQualityRating = (array) => {
  let sum = 0;
  for (let item in array) {
    sum += array[item].rating * 1;
  }
  data.avgQualityRating =
      Math.round((sum / array.length + Number.EPSILON) * 100) / 100;
}
const averageTimelinessRating = (array) => {
  let sum = 0;
  for (let item in array) {
    sum += array[item].rating * 1;
  }
  data.avgTimelinessRating =
      Math.round((sum / array.length + Number.EPSILON) * 100) / 100;
}
// --------------------

const toggleActive = (index) => {
  if (data.show.includes(index)) {
    data.show = data.show.filter((entry) => entry !== index);
    return;
  }
  data.show.push(index);
}

const editItem = (item) => {
  data.isEditing = true;
  data.editedItem = JSON.parse(JSON.stringify(item));
  toggleActive();
  data.openRatingsForm = true;
}

const deleteItem = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to delete this item?'
  );
  if (deleteMessage === true) {
    data.loading = true;
    ApiWrapper.post('ratings-categories', 'delete-rating', {
          item: item,
          personId: props.contextIdA
        })
        .then((response) => {
          listAll();
          ToastUtil.successMsg('Deleted rating');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const formatTimestampByLocale = (timestamp) => {
  const date = new Date(timestamp)
  return date.toLocaleString()
}

const newRatingQuality = () => {
  if (form.value.validate()) {
    data.loading = true;
    data.editedItem.category = 'quality';
    ApiWrapper.post('ratings-categories', 'create-rating', {
          newRating: data.editedItem,
          personId: props.contextIdA
        })
        .then((response) => {
          closeAddRating();
          data.listQualityOpen = true;
          data.loading = false;
          listAll();
          ToastUtil.successMsg('Created rating');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const newRatingTimeliness = () => {
  if (form.value.validate()) {
    data.loading = true;
    data.editedItem.category = 'timeliness';
    ApiWrapper.post('ratings-categories', 'create-rating', {
          newRating: data.editedItem,
          personId: props.contextIdA
        })
        .then((response) => {
          closeAddRating();
          data.listTimelinessOpen = true;
          data.loading = false;
          listAll();
          ToastUtil.successMsg('Created rating');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const newRatingAttitude = () => {
  if (form.value.validate()) {
    data.loading = true;
    data.editedItem.category = 'attitude';
    ApiWrapper.post('ratings-categories', 'create-rating', {
          newRating: data.editedItem,
          personId: props.contextIdA
        })
        .then((response) => {
          closeAddRating();
          data.listAttitudeOpen = true;
          data.loading = false;
          listAll();
          ToastUtil.successMsg('Created rating');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const closeAddRating = () => {
  data.openAttitudeRatingsForm = (data.openAttitudeRatingsForm = false);
  data.openQualityRatingsForm = (data.openQualityRatingsForm = false);
  data.openTimelinessRatingsForm = (data.openTimelinessRatingsForm = false);
  form.value.reset();
  nextTick(() => {
    data.editedItem = Object.assign({}, data.defaultItem);
    data.isEditing = false;
  });
}

onMounted(()=>{          

  data.loading = true;
  listAll();
})
</script>

<template>
  <v-container fluid v-if="data.loaded===true">
    <v-card class="mb-8">
      <v-toolbar density="compact" flat color="white">
        <v-toolbar-title>Ratings</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
      </v-toolbar>

      <!-- XXXXXXXXXX - Quality Rating code XXXXXXXXXX -->
      <div>
        <v-card-title class="primary--text">
          Quality
          <v-spacer></v-spacer>
          <v-btn
              v-if="!data.openQualityRatingsForm && Permissions.checkPermissions(['ratings.create'])"
              class="primary"
              size="small"
              variant="elevated"
              @click="data.openQualityRatingsForm = true"
          >
            New Rating
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="py-0">
          Average Rating &nbsp;
          <span v-if="data.avgQualityRating">({{ data.avgQualityRating }}) &nbsp;</span>
        </v-card-subtitle>
        <v-card-actions class="ml-2 pb-4 pt-0">
          <v-rating
              v-model="data.avgQualityRating"
              background-color="warning"
              color="warning"
              readonly
              density="compact"
              half-increments
              hover
              size="30"
          ></v-rating>
          <span v-if="!data.avgQualityRating"
          >&nbsp; - &nbsp; No Ratings</span
          >
          <v-btn
              variant="text"
              class="primary--text ml-2"
              @click="data.listQualityOpen=true"
              v-if="!data.listQualityOpen && Array.isArray(data.ratings) && data.ratings.length > 0"
          >
            <v-icon>mdi-chevron-down</v-icon>
            Show Ratings
          </v-btn>
          <v-btn
              variant="text"
              class="error--text ml-2"
              @click="data.listQualityOpen=false"
              v-if="data.listQualityOpen && Array.isArray(data.ratings) && data.ratings.length > 0"
          >
            <v-icon>mdi-chevron-up</v-icon>
            Hide Ratings
          </v-btn>
        </v-card-actions>

        <!-- New Note Form -->
        <v-form
            ref="form"
            lazy-validation
            v-if="data.openQualityRatingsForm"
        >
          <v-divider></v-divider>
          <v-card-actions class="px-4 pt-4 pb-0 mb-0">
            <v-input
                :value="data.editedItem.rating"
                :rules="[v => !!v || 'Rating is required']"
            >
              Select Rating - &nbsp;
              <span class="black--text text--lighten-2 caption mr-2">
                      ({{ data.editedItem.rating }})
                    </span>
              <v-rating
                  v-model="data.editedItem.rating"
                  background-color="warning"
                  color="warning"
                  density="compact"
                  half-increments
                  hover
                  size="30"
              ></v-rating>
            </v-input>
          </v-card-actions>
          <v-card-text class="px-4 pb-0">
            <v-textarea
                prepend-inner-icon="mdi-pencil"
                counter
                filled
                clearable
                clear-icon="mdi-close"
                label="Note"
                hint="No character limit"
                v-model="data.editedItem.note"
            >
            </v-textarea>
          </v-card-text>

          <v-card-actions>
            <v-btn class="error ml-2" @click="closeAddRating">Cancel</v-btn>
            <v-btn
                class="primary ml-2"
                @click="newRatingQuality"
                :disabled="data.editedItem.rating === ''"
            >Save</v-btn
            >
          </v-card-actions>

          <v-divider></v-divider>
        </v-form>

        <!-- List of Quality Ratings and Notes -->
        <div v-if="data.listQualityOpen === true && Array.isArray(data.ratings) && data.ratings.length > 0">
          <v-card-title>
            <v-text-field
                v-model="data.search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
              :headers="data.qualityHeaders"
              :items="data.ratings"
              :single-expand="data.singleExpand"
              v-model:expanded="data.expanded"
              :search="data.search"
              item-value="id"
              show-expand
              mobile-breakpoint="400"
              class="elevation-0"
          >
            <template v-slot:[`item.time`]="{ item }">
              {{ formatTimestampByLocale(item.created_at) }}
            </template>
            <template v-slot:[`item.rating`]="{ item }">
              {{ item.rating }}/5
            </template>
            <template v-slot:[`expanded-row`]="{ columns, item }">
              <td :colspan="columns.length">
                {{ item.note }}
              </td>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                  size="small"
                  v-if="Permissions.checkPermissions(['ratings.delete'])"
                  @click="deleteItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </div>
      <!-- XXXXXXXXXX - End Quality Rating Code - XXXXXXXXXX -->

      <!-- XXXXXXXXXX - Timeliness Rating code XXXXXXXXXX -->
      <div>
        <v-card-title class="primary--text">
          Timeliness
          <v-spacer></v-spacer>
          <v-btn
              v-if="!data.openTimelinessRatingsForm && Permissions.checkPermissions(['ratings.create'])"
              class="primary"
              size="small"
              variant="elevated"
              @click="data.openTimelinessRatingsForm = true"
          >
            New Rating
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="py-0">
          Average Rating &nbsp;
          <span v-if="data.avgTimelinessRating">({{ data.avgTimelinessRating }}) &nbsp;</span>
        </v-card-subtitle>

        <v-card-actions class="ml-2 pb-4 pt-0">
          <v-rating
              v-model="data.avgTimelinessRating"
              background-color="warning"
              color="warning"
              readonly
              density="compact"
              half-increments
              hover
              size="30"
          ></v-rating>
          <span v-if="!data.avgTimelinessRating"
          >&nbsp; - &nbsp; No Ratings</span
          >
          <v-btn
              variant="text"
              class="primary--text ml-2"
              @click="data.listTimelinessOpen=true"
              v-if="!data.listTimelinessOpen && Array.isArray(data.ratings2) && data.ratings2.length > 0"
          >
            <v-icon>mdi-chevron-down</v-icon>
            Show Ratings
          </v-btn>
          <v-btn
              variant="text"
              class="error--text ml-2"
              @click="data.listTimelinessOpen=false"
              v-if="data.listTimelinessOpen && Array.isArray(data.ratings2) && data.ratings2.length > 0"
          >
            <v-icon>mdi-chevron-up</v-icon>
            Hide Ratings
          </v-btn>
        </v-card-actions>

        <!-- New Note Form -->
        <v-form
            ref="form"
            lazy-validation
            v-if="data.openTimelinessRatingsForm"
        >
          <v-divider></v-divider>
          <v-card-actions class="px-4 pt-4 pb-0 mb-0">
            <v-input
                :value="data.editedItem.rating"
                :rules="[v => !!v || 'Rating is required']"
            >
              Select Rating - &nbsp;
              <span class="black--text text--lighten-2 caption mr-2">
                      ({{ data.editedItem.rating }})
                    </span>
              <v-rating
                  v-model="data.editedItem.rating"
                  background-color="warning"
                  color="warning"
                  density="compact"
                  half-increments
                  hover
                  size="30"
              ></v-rating>
            </v-input>
          </v-card-actions>
          <v-card-text class="px-4 pb-0">
            <v-textarea
                prepend-inner-icon="mdi-pencil"
                counter
                filled
                clearable
                clear-icon="mdi-close"
                label="Note"
                hint="No character limit"
                v-model="data.editedItem.note"
            >
            </v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-btn class="error ml-2" @click="closeAddRating">Cancel</v-btn>
            <v-btn
                class="primary ml-2"
                @click="newRatingTimeliness"
                :disabled="data.editedItem.rating === ''"
            >Save</v-btn
            >
          </v-card-actions>

          <v-divider></v-divider>
        </v-form>

        <!-- List of Timeliness Ratings and Notes -->
        <div v-if="data.listTimelinessOpen === true && Array.isArray(data.ratings2) && data.ratings2.length > 0">
          <v-card-title>
            <v-text-field
                v-model="data.search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
              :headers="data.timelinessHeaders"
              :items="data.ratings2"
              :single-expand="data.singleExpand"
              v-model:expanded="data.expanded"
              :search="data.search"
              item-value="id"
              show-expand
              mobile-breakpoint="400"
              class="elevation-0"
          >
            <template v-slot:[`item.time`]="{ item }">
              {{ formatTimestampByLocale(item.created_at) }}
            </template>
            <template v-slot:[`item.rating`]="{ item }">
              {{ item.rating }}/5
            </template>
            <template v-slot:[`expanded-row`]="{ columns, item }">
              <td :colspan="columns.length">
                {{ item.note }}
              </td>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                  size="small"
                  v-if="Permissions.checkPermissions(['ratings.delete'])"
                  @click="deleteItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </div>
      <!-- XXXXXXXXXX - End Timeliness Rating Code - XXXXXXXXXX -->

      <!-- XXXXXXXXXX - Attitude Rating code XXXXXXXXXX -->
      <div>
        <v-card-title class="primary--text">
          Attitude
          <v-spacer></v-spacer>
          <v-btn
              v-if="!data.openAttitudeRatingsForm && Permissions.checkPermissions(['ratings.create'])"
              class="primary"
              size="small"
              variant="elevated"
              @click="data.openAttitudeRatingsForm = true"
          >
            New Rating
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="py-0">
          Average Rating &nbsp;
          <span v-if="data.avgAttitudeRating">({{ data.avgAttitudeRating }}) &nbsp;</span>
        </v-card-subtitle>
        <v-card-actions class="ml-2 pb-4 pt-0">
          <v-rating
              v-model="data.avgAttitudeRating"
              background-color="warning"
              color="warning"
              readonly
              density="compact"
              half-increments
              hover
              size="30"
          ></v-rating>
          <span v-if="!data.avgAttitudeRating"
          >&nbsp; - &nbsp; No Ratings</span
          >
          <v-btn
              variant="text"
              class="primary--text ml-2"
              @click="data.listAttitudeOpen=true"
              v-if="!data.listAttitudeOpen && Array.isArray(data.ratings3) && data.ratings3.length > 0"
          >
            <v-icon>mdi-chevron-down</v-icon>
            Show Ratings
          </v-btn>
          <v-btn
              variant="text"
              class="error--text ml-2"
              @click="data.listAttitudeOpen=false"
              v-if="data.listAttitudeOpen && Array.isArray(data.ratings3) && data.ratings3.length > 0"
          >
            <v-icon>mdi-chevron-up</v-icon>
            Hide Ratings
          </v-btn>
        </v-card-actions>

        <!-- New Note Form -->
        <v-form
            ref="form"
            lazy-validation
            v-if="data.openAttitudeRatingsForm"
        >
          <v-divider></v-divider>
          <v-card-actions class="px-4 pt-4 pb-0 mb-0">
            <v-input
                :value="data.editedItem.rating"
                :rules="[v => !!v || 'Rating is required']"
            >
              Select Rating - &nbsp;
              <span class="black--text text--lighten-2 caption mr-2">
                      ({{ data.editedItem.rating }})
                    </span>
              <v-rating
                  v-model="data.editedItem.rating"
                  background-color="warning"
                  color="warning"
                  density="compact"
                  half-increments
                  hover
                  size="30"
              ></v-rating>
            </v-input>
          </v-card-actions>
          <v-card-text class="px-4 pb-0">
            <v-textarea
                prepend-inner-icon="mdi-pencil"
                counter
                filled
                clearable
                clear-icon="mdi-close"
                label="Note"
                hint="No character limit"
                v-model="data.editedItem.note"
            >
            </v-textarea>
          </v-card-text>

          <v-card-actions>
            <v-btn class="error ml-2" @click="closeAddRating">Cancel</v-btn>
            <v-btn
                class="primary ml-2"
                @click="newRatingAttitude"
                :disabled="data.editedItem.rating === ''"
            >Save</v-btn
            >
          </v-card-actions>

          <v-divider></v-divider>
        </v-form>

        <!-- List of Attitude Ratings and Notes -->
        <div v-if="data.listAttitudeOpen === true && Array.isArray(data.ratings3) && data.ratings3.length > 0">
          <v-card-title>
            <v-text-field
                v-model="data.search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
              :headers="data.attitudeHeaders"
              :items="data.ratings3"
              :single-expand="data.singleExpand"
              v-model:expanded="data.expanded"
              :search="data.search"
              item-value="id"
              show-expand
              mobile-breakpoint="400"
              class="elevation-0"
          >
            <template v-slot:[`item.time`]="{ item }">
              {{ formatTimestampByLocale(item.created_at) }}
            </template>
            <template v-slot:[`item.rating`]="{ item }">
              {{ item.rating }}/5
            </template>
            <template v-slot:[`expanded-row`]="{ columns, item }">
              <td :colspan="columns.length">
                {{ item.note }}
              </td>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                  size="small"
                  v-if="Permissions.checkPermissions(['ratings.delete'])"
                  @click="deleteItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </div>
      <!-- XXXXXXXXXX - End Attitude Rating Code - XXXXXXXXXX -->
    </v-card>
  </v-container>
</template>

<style scoped>
  [v-cloak] {
    display: none;
  }
  
  .container {
    padding-left: 5px;
  }
  
  .font__size__subtitle {
    font-size: 12px !important;
  }
</style>